import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import SearchComponent from '../../../../components/SearchMC';
import IconMC from '../../../../components/IconMC';

const normalizeOptions = (result) => (
  Array.isArray(result?.options) ? result.options : []
);

const CartAsyncPaginatedSelect = ({
  label,
  loadOptions,
  onSelect,
  defaultValue,
  placeholder,
  search = true,
  className = '',
  useFloatingLabel = false
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [panelPosition, setPanelPosition] = useState(null);
  const listRef = useRef(null);
  const wrapperRef = useRef(null);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  const updatePanelPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
    const panelWidth = Math.min(Math.max(rect.width, search ? 260 : rect.width), Math.max(160, vw - 16));
    const left = Math.max(8, Math.min(rect.left, Math.max(8, vw - panelWidth - 8)));
    const estimatedHeight = search ? 310 : 260;
    const positionStyles = { left, width: panelWidth };

    if (rect.bottom + estimatedHeight > vh && rect.top > estimatedHeight) {
      positionStyles.bottom = vh - rect.top + 4;
    } else {
      positionStyles.top = rect.bottom + 4;
    }

    setPanelPosition(positionStyles);
  }, [search]);

  const loadInitialOptions = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await loadOptions(searchTerm, [], { page: 1 });
      setOptions(normalizeOptions(result));
      setHasMore(Boolean(result?.hasMore));
      setPage(2);
    } catch (error) {
      console.error('Error loading cart select options:', error);
      setOptions([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [loadOptions, searchTerm]);

  const loadMoreOptions = useCallback(async () => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    try {
      const result = await loadOptions(searchTerm, options, { page });
      setOptions((prev) => [...prev, ...normalizeOptions(result)]);
      setHasMore(Boolean(result?.hasMore));
      setPage((prev) => prev + 1);
    } catch (error) {
      console.error('Error loading more cart select options:', error);
    } finally {
      setIsLoading(false);
    }
  }, [loadOptions, searchTerm, options, page, isLoading, hasMore]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPanelPosition(null);
      return undefined;
    }
    updatePanelPosition();
    return undefined;
  }, [isOpen, updatePanelPosition, options.length]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleMouseDown = (event) => {
      const insideWrapper = wrapperRef.current?.contains(event.target);
      const insidePanel = panelRef.current?.contains(event.target);
      if (insideWrapper || insidePanel) return;
      setIsOpen(false);
    };

    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isOpen, updatePanelPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;
    setOptions([]);
    setPage(1);
    setHasMore(true);
    loadInitialOptions();
    return undefined;
  }, [searchTerm, isOpen]);

  useEffect(() => {
    if (defaultValue && !selectedLabel) {
      loadOptions('', [], { page: 1 }).then((result) => {
        const found = normalizeOptions(result).find(
          (opt) => String(opt.value) === String(defaultValue)
        );
        if (found) {
          setSelectedLabel(found.label);
          setSelectedValue(found.value);
        }
      });
    }
  }, [defaultValue]);

  const handleScroll = useCallback(() => {
    if (!listRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      loadMoreOptions();
    }
  }, [loadMoreOptions]);

  const handleSelect = (option) => {
    setSelectedValue(option.value);
    setSelectedLabel(option.label);
    setIsOpen(false);
    if (onSelect) {
      onSelect(option.value, option);
    }
  };

  const rawDisplayedValue = selectedLabel || placeholder || t('customer.view_list.modal_filters.all');
  const displayedValue = (typeof rawDisplayedValue === 'string' || typeof rawDisplayedValue === 'number')
    ? rawDisplayedValue
    : '';

  const buttonClassName = [
    'tw-bg-gray-50',
    'tw-text-default',
    'tw-rounded-xl',
    'tw-w-full',
    'tw-min-w-0',
    'tw-px-4',
    'tw-flex',
    'tw-items-center',
    'tw-justify-between',
    'tw-border',
    'tw-relative',
    useFloatingLabel ? 'tw-pt-5 tw-pb-1' : 'tw-py-3'
  ].join(' ');

  const floatingLabelClassNames = [
    'tw-absolute',
    'tw-text-xs',
    'tw-text-blue-500',
    'tw-top-[5px]',
    'tw-z-10',
    'tw-start-4'
  ].join(' ');

  const portalPanelClassName = [
    'tw-bg-white',
    'tw-border',
    'tw-rounded-md',
    'tw-shadow-lg',
    'tw-overflow-hidden',
    'tw-max-w-full'
  ].join(' ');

  const listItemClassName = [
    'tw-px-4',
    'tw-py-2',
    'hover:tw-bg-blue-100',
    'tw-flex',
    'tw-items-center',
    'tw-cursor-pointer',
    'tw-break-words',
    'tw-min-w-0'
  ].join(' ');

  const fixedStyle = panelPosition
    ? {
      position: 'fixed',
      top: panelPosition.top,
      bottom: panelPosition.bottom,
      left: panelPosition.left,
      width: panelPosition.width,
      zIndex: 10006
    }
    : { display: 'none' };

  const dropdownPanel = (
    <div className="tw-wrapper" style={fixedStyle}>
      <div ref={panelRef} className={portalPanelClassName}>
        {search && (
          <div className="tw-px-2 tw-pt-2">
            <SearchComponent
              placeholder="Buscar..."
              onChange={setSearchTerm}
              inputClassName="tw-text-sm tw-bg-white"
              debounceTime={300}
            />
          </div>
        )}
        <ul
          ref={listRef}
          onScroll={handleScroll}
          className="tw-list-none tw-m-0 tw-py-1 tw-overflow-y-auto tw-max-h-60 tw-overflow-x-hidden tw-pl-0"
        >
          {options.length === 0 && !isLoading ? (
            <li className="tw-px-4 tw-py-2 tw-text-gray-500">
              {t('dropdown_mc.no_options')}
            </li>
          ) : (
            options.map((option, index) => (
              <li
                key={option.value != null ? String(option.value) : `option-${index}`}
                onClick={() => handleSelect(option)}
                className={`${listItemClassName} ${
                  String(selectedValue) === String(option.value)
                    ? 'tw-bg-blue-500 tw-text-white'
                    : ''
                }`}
              >
                <span className="tw-overflow-hidden tw-block tw-text-ellipsis tw-whitespace-nowrap tw-w-full">
                  {typeof option.label === 'string' || typeof option.label === 'number'
                    ? option.label
                    : ''}
                </span>
              </li>
            ))
          )}
          {isLoading && (
            <li className="tw-px-4 tw-py-2 tw-text-gray-500 tw-text-center">
              {t('customer.view_list.modal_filters.loading')}
            </li>
          )}
        </ul>
      </div>
    </div>
  );

  return (
    <div className={`tw-wrapper tw-relative tw-w-full tw-min-w-0 ${className}`} ref={wrapperRef}>
      {!useFloatingLabel && label && (
        <label className="tw-text-zinc-700 tw-mb-1 tw-text-sm tw-font-bold">
          {label}
        </label>
      )}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={buttonClassName}
      >
        <div className="tw-w-full tw-min-w-0 tw-pr-6">
          {useFloatingLabel && label && (
            <label className={floatingLabelClassNames}>
              {label}
            </label>
          )}
          <span className="tw-flex tw-items-center tw-overflow-hidden tw-w-full tw-min-w-0">
            <span className="tw-block tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
              {displayedValue}
            </span>
          </span>
          <div className="tw-absolute tw-top-[34%] tw-right-4">
            <IconMC
              name={`arrow-ios-${isOpen ? 'up' : 'down'}ward-outline`}
              size="base"
              className="tw-text-gray-600"
            />
          </div>
        </div>
      </button>

      {isOpen && panelPosition && typeof document !== 'undefined'
        && createPortal(dropdownPanel, document.body)}
    </div>
  );
};

export default CartAsyncPaginatedSelect;
